import React, { useState, useEffect, useRef } from "react";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { FaInstagram, FaWhatsapp, FaSearch } from "react-icons/fa";
import "../styles/contact.css";
import { useNavigate } from "react-router-dom";

gsap.registerPlugin(ScrollTrigger);

/* ════════════════════════════════════════
   CUSTOM CALENDAR COMPONENT
   ════════════════════════════════════════ */
const CustomCalendar = ({ value, onChange, minDate }) => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const parseDate = (ds) => {
    if (!ds) return null;
    const [y, m, d] = ds.split("-").map(Number);
    return new Date(y, m - 1, d);
  };

  const selectedDate = parseDate(value);
  const minD = parseDate(minDate) || today;

  const [viewYear, setViewYear] = useState(
    selectedDate ? selectedDate.getFullYear() : today.getFullYear()
  );
  const [viewMonth, setViewMonth] = useState(
    selectedDate ? selectedDate.getMonth() : today.getMonth()
  );
  const [isOpen, setIsOpen] = useState(false);
  const [animating, setAnimating] = useState(false);
  const [slideDir, setSlideDir] = useState(1);
  const calRef = useRef(null);
  const gridRef = useRef(null);

  const MONTHS = ["January", "February", "March", "April", "May", "June",
    "July", "August", "September", "October", "November", "December"];
  const DAYS = ["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"];

  const formatDisplay = (ds) => {
    if (!ds) return "Select a date";
    const [y, m, d] = ds.split("-").map(Number);
    const dt = new Date(y, m - 1, d);
    return dt.toLocaleDateString("en-IN", { weekday: "short", day: "numeric", month: "long", year: "numeric" });
  };

  const getDaysInMonth = (y, m) => new Date(y, m + 1, 0).getDate();
  const getFirstDayOfMonth = (y, m) => new Date(y, m, 1).getDay();

  const toDateString = (y, m, d) => `${y}-${String(m + 1).padStart(2, "0")}-${String(d).padStart(2, "0")}`;

  const isDisabled = (y, m, d) => {
    const dt = new Date(y, m, d);
    dt.setHours(0, 0, 0, 0);
    return dt < minD;
  };

  const isSelected = (y, m, d) => {
    if (!selectedDate) return false;
    return selectedDate.getFullYear() === y && selectedDate.getMonth() === m && selectedDate.getDate() === d;
  };

  const isToday = (y, m, d) => {
    return today.getFullYear() === y && today.getMonth() === m && today.getDate() === d;
  };

  const handlePrev = () => {
    setSlideDir(-1);
    setAnimating(true);
    setTimeout(() => {
      setViewMonth(prev => {
        if (prev === 0) { setViewYear(y => y - 1); return 11; }
        return prev - 1;
      });
      setAnimating(false);
    }, 180);
  };

  const handleNext = () => {
    setSlideDir(1);
    setAnimating(true);
    setTimeout(() => {
      setViewMonth(prev => {
        if (prev === 11) { setViewYear(y => y + 1); return 0; }
        return prev + 1;
      });
      setAnimating(false);
    }, 180);
  };

  const handleDayClick = (y, m, d) => {
    if (isDisabled(y, m, d)) return;
    onChange(toDateString(y, m, d));
    setIsOpen(false);
  };

  useEffect(() => {
    const handler = (e) => {
      if (calRef.current && !calRef.current.contains(e.target)) setIsOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const daysInMonth = getDaysInMonth(viewYear, viewMonth);
  const firstDay = getFirstDayOfMonth(viewYear, viewMonth);
  const cells = [];

  for (let i = 0; i < firstDay; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);

  return (
    <div className="kq-cal-wrap" ref={calRef}>
      <button
        type="button"
        className={`kq-cal-trigger ${value ? "kq-cal-has-value" : ""}`}
        onClick={() => setIsOpen(o => !o)}
      >
        <span className="kq-cal-trigger-icon">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <rect x="3" y="4" width="18" height="18" rx="2" />
            <line x1="16" y1="2" x2="16" y2="6" />
            <line x1="8" y1="2" x2="8" y2="6" />
            <line x1="3" y1="10" x2="21" y2="10" />
          </svg>
        </span>
        <span className="kq-cal-trigger-text">{formatDisplay(value)}</span>
        <span className="kq-cal-trigger-arrow" style={{ transform: isOpen ? "rotate(180deg)" : "rotate(0deg)" }}>
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
            <polyline points="6 9 12 15 18 9" />
          </svg>
        </span>
      </button>

      {isOpen && (
        <div className="kq-cal-dropdown">
          <div className="kq-cal-header">
            <button type="button" className="kq-cal-nav" onClick={handlePrev}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                <polyline points="15 18 9 12 15 6" />
              </svg>
            </button>
            <span className="kq-cal-month-label">
              {MONTHS[viewMonth]} <strong>{viewYear}</strong>
            </span>
            <button type="button" className="kq-cal-nav" onClick={handleNext}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                <polyline points="9 18 15 12 9 6" />
              </svg>
            </button>
          </div>

          <div className="kq-cal-daynames">
            {DAYS.map(d => <span key={d}>{d}</span>)}
          </div>

          <div
            className={`kq-cal-grid ${animating ? (slideDir > 0 ? "kq-cal-slide-out-left" : "kq-cal-slide-out-right") : ""}`}
            ref={gridRef}
          >
            {cells.map((d, i) => (
              <button
                key={i}
                type="button"
                className={[
                  "kq-cal-day",
                  d === null ? "kq-cal-empty" : "",
                  d && isDisabled(viewYear, viewMonth, d) ? "kq-cal-disabled" : "",
                  d && isSelected(viewYear, viewMonth, d) ? "kq-cal-selected" : "",
                  d && isToday(viewYear, viewMonth, d) ? "kq-cal-today" : "",
                ].filter(Boolean).join(" ")}
                onClick={() => d && handleDayClick(viewYear, viewMonth, d)}
                disabled={d === null || isDisabled(viewYear, viewMonth, d)}
              >
                {d || ""}
              </button>
            ))}
          </div>

          <div className="kq-cal-footer">
            <button
              type="button"
              className="kq-cal-today-btn"
              onClick={() => {
                const ds = toDateString(today.getFullYear(), today.getMonth(), today.getDate());
                if (!isDisabled(today.getFullYear(), today.getMonth(), today.getDate())) {
                  onChange(ds);
                  setIsOpen(false);
                }
              }}
            >
              Today
            </button>
            <button type="button" className="kq-cal-clear-btn" onClick={() => { onChange(""); setIsOpen(false); }}>
              Clear
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

/* ════════════════════════════════════════
   PHONE INPUT — strictly 10 digits only
   ════════════════════════════════════════ */
const PhoneInput = ({ value, onChange }) => {
  const handleChange = (e) => {
    const digits = e.target.value.replace(/\D/g, "").slice(0, 10);
    onChange(digits);
  };

  const handleKeyDown = (e) => {
    const allowed = ["Backspace", "Delete", "Tab", "Escape", "Enter", "ArrowLeft", "ArrowRight", "ArrowUp", "ArrowDown", "Home", "End"];
    if (allowed.includes(e.key)) return;
    if (!/^\d$/.test(e.key)) {
      e.preventDefault();
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const pasted = e.clipboardData.getData("text").replace(/\D/g, "").slice(0, 10);
    onChange(pasted);
  };

  const isValid = value.length === 10;
  const isEmpty = value.length === 0;

  return (
    <div className="kq-phone-wrap">
      <div className={`kq-phone-input-row ${!isEmpty && !isValid ? "kq-phone-error" : ""} ${isValid ? "kq-phone-valid" : ""}`}>
        <span className="kq-phone-prefix">+91</span>
        <input
          type="tel"
          inputMode="numeric"
          value={value}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
          onPaste={handlePaste}
          placeholder="9876543210"
          maxLength={10}
          required
          className="kq-phone-field"
          autoComplete="tel"
        />
      </div>
      {!isEmpty && !isValid && (
        <p className="kq-phone-hint">Enter exactly 10 digits ({10 - value.length} more needed)</p>
      )}
    </div>
  );
};

/* ════════════════════════════════════════
   MULTI-SELECT DROPDOWN WITH SEARCH
   ════════════════════════════════════════ */
const MultiSelectDropdown = ({ options, selectedValues, onChange, placeholder }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const dropdownRef = useRef(null);
  const searchInputRef = useRef(null);

  // Filter options based on search term
  const filteredOptions = options.filter(opt =>
    opt.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Group options by category for better organization
  const getGroupedOptions = () => {
    const grouped = {};
    filteredOptions.forEach(opt => {
      const category = opt.split(" - ")[0];
      if (!grouped[category]) grouped[category] = [];
      grouped[category].push(opt);
    });
    return grouped;
  };

  const toggleOption = (option) => {
    if (selectedValues.includes(option)) {
      onChange(selectedValues.filter(v => v !== option));
    } else {
      onChange([...selectedValues, option]);
    }
  };

  const removeOption = (option) => {
    onChange(selectedValues.filter(v => v !== option));
  };

  const clearAll = () => {
    onChange([]);
  };

  // Auto-focus search input when dropdown opens
  useEffect(() => {
    if (isOpen && searchInputRef.current) {
      setTimeout(() => searchInputRef.current.focus(), 100);
    }
  }, [isOpen]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handler = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setIsOpen(false);
        setSearchTerm("");
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  // Highlight matching text in option
  const highlightMatch = (text, search) => {
    if (!search) return text;
    const regex = new RegExp(`(${search.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'gi');
    const parts = text.split(regex);
    return parts.map((part, i) =>
      regex.test(part) ? <mark key={i} className="kq-search-highlight">{part}</mark> : part
    );
  };

  const groupedOptions = getGroupedOptions();
  const hasResults = Object.keys(groupedOptions).length > 0;

  return (
    <div className="kq-multiselect-wrap" ref={dropdownRef}>
      <div
        className={`kq-multiselect-trigger ${selectedValues.length > 0 ? "kq-has-value" : ""}`}
        onClick={() => setIsOpen(!isOpen)}
      >
        <div className="kq-multiselect-tags">
          {selectedValues.length === 0 ? (
            <span className="kq-multiselect-placeholder">{placeholder || "Select services..."}</span>
          ) : (
            selectedValues.map((val, idx) => (
              <span key={idx} className="kq-multiselect-tag">
                {val.length > 30 ? val.substring(0, 30) + "..." : val}
                <button
                  type="button"
                  className="kq-multiselect-tag-remove"
                  onClick={(e) => {
                    e.stopPropagation();
                    removeOption(val);
                  }}
                >
                  ✕
                </button>
              </span>
            ))
          )}
        </div>
        <div className="kq-multiselect-icons">
          {selectedValues.length > 0 && (
            <span
              className="kq-multiselect-clear-icon"
              onClick={(e) => {
                e.stopPropagation();
                clearAll();
              }}
            >
              ✕
            </span>
          )}
          <span className="kq-multiselect-arrow" style={{ transform: isOpen ? "rotate(180deg)" : "rotate(0deg)" }}>
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
              <polyline points="6 9 12 15 18 9" />
            </svg>
          </span>
        </div>
      </div>

      {isOpen && (
        <div className="kq-multiselect-dropdown">
          {/* Search Bar */}
          <div className="kq-multiselect-search">
            <FaSearch className="kq-search-icon" />
            <input
              ref={searchInputRef}
              type="text"
              placeholder="Search services by name, category..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onClick={(e) => e.stopPropagation()}
            />
            {searchTerm && (
              <button
                className="kq-search-clear"
                onClick={() => setSearchTerm("")}
              >
                ✕
              </button>
            )}
          </div>

          {/* Results count */}
          {searchTerm && (
            <div className="kq-search-results-count">
              Found {filteredOptions.length} result{filteredOptions.length !== 1 ? 's' : ''} for "{searchTerm}"
            </div>
          )}

          {/* Options list with grouping */}
          <div className="kq-multiselect-options">
            {!hasResults ? (
              <div className="kq-multiselect-no-results">
                <span>🔍</span>
                <p>No services found matching "{searchTerm}"</p>
                <small>Try a different search term</small>
              </div>
            ) : (
              Object.entries(groupedOptions).map(([category, opts]) => (
                <div key={category} className="kq-multiselect-group">
                  <div className="kq-multiselect-group-header">
                    <span className="kq-group-icon">
                      {category === "Women" ? "👩" : "👨"}
                    </span>
                    <span>{category}</span>
                    <span className="kq-group-count">({opts.length})</span>
                  </div>
                  <div className="kq-multiselect-group-options">
                    {opts.map((opt, idx) => (
                      <label key={idx} className="kq-multiselect-option">
                        <input
                          type="checkbox"
                          checked={selectedValues.includes(opt)}
                          onChange={() => toggleOption(opt)}
                        />
                        <span>{highlightMatch(opt, searchTerm)}</span>
                      </label>
                    ))}
                  </div>
                </div>
              ))
            )}
          </div>

          {/* Footer with actions */}
          <div className="kq-multiselect-footer">
            <div className="kq-multiselect-footer-left">
              {selectedValues.length > 0 && (
                <span className="kq-selected-count">
                  {selectedValues.length} selected
                </span>
              )}
            </div>
            <div className="kq-multiselect-footer-right">
              {selectedValues.length > 0 && (
                <button
                  type="button"
                  className="kq-multiselect-clear"
                  onClick={clearAll}
                >
                  Clear all
                </button>
              )}
              <button
                type="button"
                className="kq-multiselect-done"
                onClick={() => setIsOpen(false)}
              >
                Done
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

/* ════════════════════════════════════════
   MAIN CONTACT COMPONENT
   ════════════════════════════════════════ */
const Contact = () => {
  const navigate = useNavigate();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [date, setDate] = useState("");
  const [services, setServices] = useState([]);
  const [slot, setSlot] = useState("");
  const [bookedSlotsData, setBookedSlotsData] = useState([]);
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [formStatus, setFormStatus] = useState({ submitted: false, success: false, message: "" });
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalBookings, setModalBookings] = useState([]);
  const [loadingModal, setLoadingModal] = useState(false);
  const [modalDate, setModalDate] = useState("");
  const [selectedModalDate, setSelectedModalDate] = useState("");
  const pageRef = useRef(null);
  const formRef = useRef(null);
  const modalRef = useRef(null);

  const shopDetails = {
    name: "King and Queen Salon",
    address: "SM Arcade, 2nd Floor, Near Casaba Police Station",
    area: "Pudussery, Palakkad, Kerala, India",
    phone: "+91 88910 64655",
    email: "suresh94475@gmail.com",
    whatsapp: "918891064655",
    hours: {
      Monday: "9:00 AM – 9:00 PM",
      Tuesday: "9:00 AM – 9:00 PM",
      Wednesday: "9:00 AM – 9:00 PM",
      Thursday: "9:00 AM – 9:00 PM",
      Friday: "9:00 AM – 9:00 PM",
      Saturday: "9:00 AM – 9:00 PM",
      Sunday: "9:00 AM – 6:00 PM",
    },
  };

  const socialLinks = [
    { icon: FaInstagram, url: "https://www.instagram.com/king_queen_pkd/", name: "Instagram", color: "#E4405F" },
    { icon: FaWhatsapp, url: "https://wa.me/918891064655", name: "WhatsApp", color: "#25D366" },
  ];

  const servicesData = {
    Women: [
      { category: "Hair Cut", icon: "✂", image: "/assets/service/hc.png", services: [{ name: "Front Flick", price: "200" }, { name: "Boy Cut", price: "400" }, { name: "Blunt & Bob", price: "500" }, { name: "Straight & U Cut", price: "550" }, { name: "Layer Cut", price: "600" }] },
      { category: "Threading", icon: "〰", image: "/assets/service/thread.jpg", services: [{ name: "Forehead", price: "10" }, { name: "Upper Lip", price: "10" }, { name: "Chin", price: "10" }, { name: "Side Locks", price: "40" }, { name: "Eye Brow", price: "50" }, { name: "Full Face", price: "150" }] },
      { category: "Waxing", icon: "◈", image: "/assets/service/wax.jpg", isTwoPrice: true, label1: "Regular", label2: "Premium", services: [{ name: "Chin", price: "30", price1: "10" }, { name: "Upper Lip", price: "30", price1: "10" }, { name: "For Head", price: "60", price1: "10" }, { name: "Side Lock", price: "100", price1: "80" }, { name: "Under Arms", price: "200", price1: "150" }, { name: "Full Face", price: "250", price1: "200" }, { name: "Half Arms", price: "400", price1: "300" }, { name: "Full Arms", price: "500", price1: "400" }, { name: "Half Leg", price: "600", price1: "500" }, { name: "Full Leg", price: "1300", price1: "1100" }, { name: "Full Body", price: "5000", price1: "3000" }] },
      { category: "Hair Color", icon: "◎", image: "/assets/service/hc.png", services: [{ name: "Garnier", price: "1000" }, { name: "Matrix", price: "1500" }, { name: "Loreal", price: "2000" }, { name: "Schwarzkope", price: "1600" }, { name: "Speedy", price: "1800" }, { name: "Amonia Free", price: "2000" }, { name: "Top Speedy", price: "2000" }] },
      { category: "Facial", icon: "✦", image: "/assets/service/facial.jpg", services: [{ name: "Basic Facial", price: "700" }, { name: "Fruit Facial", price: "1000" }, { name: "Aroma Facial", price: "1200" }, { name: "Anti Ageing", price: "1500" }, { name: "Silver Facial", price: "1600" }, { name: "Ordinary Gold", price: "1700" }, { name: "Ordinary Pearl", price: "1800" }, { name: "Tan Removal", price: "1800" }, { name: "Skin Whitening", price: "1900" }, { name: "Oxy Vitalife", price: "2000" }, { name: "Diamond", price: "2100" }, { name: "Hydra Facial", price: "3700" }] },
      { category: "Bleach & D-Tan", icon: "◇", image: "/assets/service/DTAN.webp", isBleach: true, services: [{ name: "Under Arms", oxy: "150", fruit: "200", gold: "300", dtan: "400" }, { name: "Neck", oxy: "200", fruit: "300", gold: "400", dtan: "500" }, { name: "Face", oxy: "350", fruit: "400", gold: "500", dtan: "600" }, { name: "Foot", oxy: "400", fruit: "500", gold: "600", dtan: "700" }, { name: "Half Hand", oxy: "500", fruit: "600", gold: "700", dtan: "800" }, { name: "Half Leg", oxy: "600", fruit: "700", gold: "800", dtan: "900" }, { name: "Full Hand", oxy: "900", fruit: "1000", gold: "1100", dtan: "1200" }, { name: "Full Leg", oxy: "1300", fruit: "1400", gold: "1500", dtan: "1600" }, { name: "Full Body", oxy: "4500", fruit: "5000", gold: "5500", dtan: "6000" }] },
      { category: "Hair Highlighting", icon: "✧", image: "/assets/service/hairhigh.webp", services: [{ name: "One Strip", price: "300" }, { name: "Highlight", price: "1500" }, { name: "Global Colour", price: "2000" }] },
      { category: "Body", icon: "◉", image: "/assets/service/body.avif", services: [{ name: "Ear Lobe Piercing" }, { name: "Wart Removal" }, { name: "Tattoos" }, { name: "Body Piercing" }] },
      { category: "Massage", icon: "⊕", image: "/assets/service/masagewomen.jpg", services: [{ name: "Face", price: "350" }, { name: "Head", price: "400" }] },
      { category: "Cleanup", icon: "❋", image: "/assets/service/cleanup.jpg", services: [{ name: "Normal", price: "450" }, { name: "Fruit", price: "550" }, { name: "Aroma", price: "700" }, { name: "Silver", price: "800" }, { name: "Gold", price: "900" }, { name: "Pearl", price: "1100" }, { name: "Diamond", price: "1300" }, { name: "Hydra Cleanup", price: "1800" }] },
      { category: "Hair Spa", icon: "◌", image: "/assets/service/hairspa.webp", isSpa: true, services: [{ name: "Normal Spa", small: "1000", large: "1200" }, { name: "Black Spa", small: "1100", large: "1300" }, { name: "Repair", small: "1000", large: "1200" }, { name: "Colour Care", small: "1000", large: "1200" }, { name: "Smoothening", small: "1100", large: "1300" }, { name: "Dandruff", small: "1300", large: "1500" }, { name: "Keratin", small: "1600", large: "1800" }, { name: "Nano Plasta", small: "1900", large: "2100" }] },
      { category: "Shahanaz", icon: "⊛", image: "/assets/service/shahnaz.avif", services: [{ name: "Veg Peel", price: "1000" }, { name: "Shahanaz Normal", price: "1700" }, { name: "Shahanaz Pearl", price: "2500" }, { name: "Shahanaz Gold", price: "2800" }, { name: "Shahanaz Diamond", price: "3000" }] },
      { category: "Henna", icon: "❀", image: "/assets/service/hena.jpg", services: [{ name: "Natural Henna", price: "800" }, { name: "Colour Henna", price: "750" }, { name: "Black Henna", price: "700" }] },
      { category: "Manicure", icon: "◈", image: "/assets/service/manicure.avif", services: [{ name: "Normal", price: "600" }, { name: "Spa Manicure", price: "800" }, { name: "Aroma", price: "1000" }, { name: "Bloom Manicure", price: "1200" }, { name: "K&Q SPL", price: "1500" }] },
      { category: "Pedicure", icon: "◈", image: "/assets/service/pedicure.jpg", services: [{ name: "Normal", price: "1000" }, { name: "Spa Pedicure", price: "1500" }, { name: "Aroma", price: "1700" }, { name: "Bloom Pedicure", price: "1900" }, { name: "K&Q SPL", price: "2100" }] },
      { category: "Hair Treatment", icon: "✦", image: "/assets/service/hairtreatment.jpg", services: [{ name: "Blow Dry", price: "450" }, { name: "Ironing", price: "800" }, { name: "Rebonding", price: "3000" }, { name: "Smoothening", price: "5000" }, { name: "Straightening", price: "4500" }, { name: "Permanent Blow Dry", price: "5000" }, { name: "Keratin", price: "6000" }, { name: "Nano Plasta", price: "7000" }] },
      { category: "K&Q Special", icon: "★", image: "/assets/service/hydra.jpg", services: [{ name: "Hydra Facial", price: "On Consultation" }] },
    ],
    Men: [
      { category: "Hair Cut", icon: "✂", image: "/assets/service/hc.png", services: [{ name: "Kids", price: "150" }, { name: "Gents", price: "180" }, { name: "Beard Trimming", price: "150" }, { name: "Shaving", price: "120" }, { name: "Change Of Hairstyle", price: "300" }] },
      { category: "Henna", icon: "❀", image: "/assets/service/mens-henna.avif", services: [{ name: "Black", price: "400" }, { name: "Colour Henna", price: "450" }, { name: "Natural Henna", price: "500" }] },
      { category: "Clean Up", icon: "❋", image: "/assets/service/mens-wash.jpg", services: [{ name: "Normal", price: "450" }, { name: "Fruit", price: "550" }, { name: "Aroma", price: "650" }, { name: "Silver", price: "750" }, { name: "Gold", price: "850" }] },
      { category: "Massage", icon: "⊕", image: "/assets/service/masagemen.jpg", services: [{ name: "Face", price: "350" }, { name: "Head", price: "350" }] },
      { category: "Facial", icon: "✦", image: "/assets/service/mens-facial.jpg", services: [{ name: "Basic Facial", price: "800" }, { name: "Fruit Facial", price: "1000" }, { name: "Aroma Facial", price: "1200" }, { name: "Anti Ageing", price: "1400" }, { name: "Silver Facial", price: "1500" }, { name: "Ordinary Gold", price: "1600" }, { name: "Ordinary Pearl", price: "1700" }, { name: "Tan Removal", price: "1700" }, { name: "Skin Whitening", price: "1800" }, { name: "Oxy Vitalife", price: "1900" }, { name: "Diamond", price: "2000" }, { name: "Hydra Facial", price: "3500" }] },
      { category: "Hair Spa", icon: "◌", image: "/assets/service/hairspa.webp", services: [{ name: "Normal Spa", price: "1000" }, { name: "Black Spa", price: "1100" }, { name: "Repair", price: "1000" }, { name: "Colour Care", price: "1000" }, { name: "Smoothening", price: "1100" }, { name: "Dandruff", price: "1300" }, { name: "Keratin", price: "1600" }, { name: "Nano Plasta", price: "1900" }] },
      { category: "Bleach & D-Tan", icon: "◇", image: "/assets/service/DTAN.webp", isBleach: true, services: [{ name: "Face", oxy: "400", fruit: "450", gold: "550", dtan: "650" }, { name: "Foot", oxy: "450", fruit: "550", gold: "650", dtan: "750" }, { name: "Half Hand", oxy: "550", fruit: "650", gold: "750", dtan: "850" }, { name: "Half Leg", oxy: "650", fruit: "750", gold: "850", dtan: "950" }, { name: "Full Hand", oxy: "950", fruit: "1100", gold: "1100", dtan: "1200" }] },
      { category: "Shahanaz", icon: "⊛", image: "/assets/service/shahnaz.avif", services: [{ name: "Veg Peel", price: "900" }, { name: "Shahanaz Normal", price: "1600" }, { name: "Shahanaz Pearl", price: "2400" }, { name: "Shahanaz Gold", price: "2700" }, { name: "Shahanaz Diamond", price: "2900" }] },
      { category: "Body", icon: "◉", image: "/assets/service/body.avif", services: [{ name: "Ear Lobe Piercing" }, { name: "Wart Removal" }, { name: "Tattoos" }, { name: "Body Piercing" }] },
      { category: "Manicure", icon: "◈", image: "/assets/service/manicure.avif", services: [{ name: "Normal", price: "500" }, { name: "Spa Manicure", price: "700" }, { name: "Aroma", price: "900" }, { name: "Bloom Manicure", price: "1100" }] },
      { category: "Pedicure", icon: "◈", image: "/assets/service/pedicure.jpg", services: [{ name: "Normal", price: "900" }, { name: "Spa Pedicure", price: "1400" }, { name: "Aroma", price: "1600" }, { name: "Bloom Pedicure", price: "1800" }] },
      { category: "Hair Treatment", icon: "✦", image: "/assets/service/hairtreatment.jpg", services: [{ name: "Blow Dry", price: "200" }, { name: "Ironing", price: "400" }, { name: "Rebonding", price: "1600" }, { name: "Smoothening", price: "1800" }, { name: "Straightening", price: "1900" }, { name: "Permanent Blow Dry", price: "2200" }, { name: "Keratin", price: "2500" }, { name: "Nano Plasta", price: "2800" }, { name: "Micro Bonding", price: "3000" }] },
      { category: "Hair Highlighting", icon: "✧", image: "/assets/service/hairhigh.webp", services: [{ name: "Garnier", price: "450" }, { name: "Matrix", price: "600" }, { name: "Loreal", price: "650" }, { name: "Schwarzkopp", price: "650" }, { name: "Speedy", price: "750" }, { name: "Amonia Free", price: "850" }] },
      { category: "Groom Touch Up", icon: "◎", image: "/assets/service/groom.jpg", services: [{ name: "Party", price: "1600" }, { name: "Shaving", price: "1000" }] },
      { category: "K&Q Special", icon: "★", image: "/assets/service/hydra.jpg", services: [{ name: "Hydra Facial", price: "3500" }] },
    ],
  };

  const today = new Date().toISOString().split("T")[0];

  const formatDate = (ds) => {
    if (!ds) return "";
    return new Date(ds).toLocaleDateString(undefined, { year: "numeric", month: "long", day: "numeric" });
  };

  const generateAllSlots = () => {
    const slots = [];
    const selectedDate = new Date(date);
    const day = selectedDate.toLocaleDateString("en-US", { weekday: "long" });
    let start = new Date(); start.setHours(9, 0, 0);
    let end = new Date();
    if (day === "Sunday") { end.setHours(18, 0, 0); } else { end.setHours(21, 0, 0); }
    const fmt = (t) => t.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
    while (start < end) {
      const next = new Date(start.getTime() + 30 * 60000);
      slots.push(`${fmt(start)} - ${fmt(next)}`);
      start = next;
    }
    return slots;
  };

  const generateServiceOptions = () => {
    const options = [];
    Object.entries(servicesData).forEach(([gender, categories]) => {
      categories.forEach((cat) => {
        if (!cat.services) return;
        cat.services.forEach((srv) => {
          options.push(`${gender} - ${cat.category} (${srv.name})`);
        });
      });
    });
    return options;
  };

  const allServices = generateServiceOptions();

  const getBookedSlotsForDate = () => {
    if (!date || !bookedSlotsData.length) return [];
    return bookedSlotsData.map((b) => b.slot);
  };

  const getAvailableSlots = () => {
    const all = generateAllSlots();
    if (!date) return all;
    const booked = getBookedSlotsForDate();
    return all.filter((s) => !booked.includes(s));
  };

  const fetchBookingsForDate = async (d) => {
    if (!d) return;
    setLoadingModal(true);
    try {
      const res = await fetch(`https://salon.mpdatahub.com/api/slots?date=${d}`);
      const data = await res.json();
      setModalBookings(data.data && Array.isArray(data.data) ? data.data : []);
      if (!isModalOpen) {
        setIsModalOpen(true);
        setTimeout(() => {
          if (modalRef.current) {
            gsap.fromTo(modalRef.current, { opacity: 0, scale: 0.9 }, { opacity: 1, scale: 1, duration: 0.35, ease: "back.out(1.7)" });
          }
        }, 50);
      }
    } catch { setModalBookings([]); }
    finally { setLoadingModal(false); }
  };

  const openTodayModal = () => {
    setSelectedModalDate(today);
    setModalDate(today);
    fetchBookingsForDate(today);
  };

  const handleModalDateChange = (e) => {
    const d = e.target.value;
    setSelectedModalDate(d);
    setModalDate(d);
    fetchBookingsForDate(d);
  };

  const closeModal = () => {
    gsap.to(modalRef.current, {
      opacity: 0, scale: 0.9, duration: 0.2, ease: "power2.in",
      onComplete: () => { setIsModalOpen(false); setModalBookings([]); setSelectedModalDate(""); },
    });
  };

  useEffect(() => {
    const fetchSlots = async () => {
      if (!date) { setBookedSlotsData([]); return; }
      setLoadingSlots(true);
      setSlot("");
      try {
        const res = await fetch(`https://salon.mpdatahub.com/api/slots?date=${date}`);
        const data = await res.json();
        setBookedSlotsData(data.data && Array.isArray(data.data) ? data.data : []);
      } catch { setBookedSlotsData([]); }
      finally { setLoadingSlots(false); }
    };
    fetchSlots();
  }, [date]);

  useEffect(() => {
    const handleEsc = (e) => { if (e.key === "Escape" && isModalOpen) closeModal(); };
    document.addEventListener("keydown", handleEsc);
    return () => document.removeEventListener("keydown", handleEsc);
  }, [isModalOpen]);

  useEffect(() => {
    const ctx = gsap.context(() => {
      gsap.from(".ct-hero-line", { yPercent: 110, opacity: 0, duration: 1.3, stagger: 0.16, ease: "expo.out", delay: 0.2 });
      gsap.from(".ct-hero-sub", { opacity: 0, y: 22, duration: 1, delay: 0.9, ease: "power3.out" });
      gsap.utils.toArray(".ct-reveal").forEach((el) => {
        gsap.from(el, {
          scrollTrigger: { trigger: el, start: "top 88%", toggleActions: "play none none none" },
          y: 50, opacity: 0, duration: 0.9, ease: "power3.out",
        });
      });
      gsap.utils.toArray(".ct-stagger-parent").forEach((p) => {
        const kids = p.querySelectorAll(".ct-stagger-child");
        gsap.from(kids, {
          scrollTrigger: { trigger: p, start: "top 85%", toggleActions: "play none none none" },
          y: 40, opacity: 0, duration: 0.7, stagger: 0.1, ease: "power2.out",
        });
      });
    }, pageRef);
    return () => ctx.revert();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!/^\d{10}$/.test(phone)) {
      setFormStatus({ submitted: true, success: false, message: "Please enter a valid 10-digit mobile number." });
      gsap.to(formRef.current, { x: [-8, 8, -8, 8, 0], duration: 0.4, ease: "power2.inOut" });
      return;
    }

    if (services.length === 0) {
      setFormStatus({ submitted: true, success: false, message: "Please select at least one service." });
      gsap.to(formRef.current, { x: [-8, 8, -8, 8, 0], duration: 0.4, ease: "power2.inOut" });
      return;
    }

    const servicesString = services.join(", ");

    const data = {
      name,
      email,
      phone,
      date,
      service: servicesString,
      services: services,
      slot
    };

    try {
      const res = await fetch("https://salon.mpdatahub.com/api/book-slot", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(data),
      });
      const result = await res.json();
      alert(result.message);
      if (result.data || result) {
        setFormStatus({ submitted: true, success: true, message: result.message || "Booking successful!" });
        const bd = result.data || data;
        const msg = encodeURIComponent(
          `*New Slot Booking — King & Queen*\n\n` +
          `Name: ${bd.name}\nPhone: ${bd.phone}\nEmail: ${bd.email}\nService(s): ${servicesString}\nDate: ${bd.date}\nSlot: ${bd.slot}`
        );
        window.open(`https://wa.me/${shopDetails.whatsapp}?text=${msg}`, "_blank");
        setTimeout(() => {
          setName(""); setEmail(""); setPhone(""); setDate(""); setServices([]); setSlot("");
          setBookedSlotsData([]);
          setFormStatus({ submitted: false, success: false, message: "" });
        }, 3000);
      } else {
        setFormStatus({ submitted: true, success: false, message: result.message || "Error booking slot." });
        gsap.to(formRef.current, { x: [-8, 8, -8, 8, 0], duration: 0.4, ease: "power2.inOut" });
      }
    } catch {
      alert("Network error. Please try again.");
      setFormStatus({ submitted: true, success: false, message: "Network error. Please try again." });
      gsap.to(formRef.current, { x: [-8, 8, -8, 8, 0], duration: 0.4, ease: "power2.inOut" });
    }
  };

  const availableSlotsForDisplay = getAvailableSlots();

  return (
    <div className="ct-root" ref={pageRef}>
      {/* HERO SECTION */}
      <section className="ct-hero">
        <div className="ct-hero-bg">
          <img src="/assets/imagess/model1.jpg" alt="contact" className="ct-hero-img" loading="eager" decoding="sync" />
          <div className="ct-hero-overlay" />
        </div>
        <div className="ct-hero-content">
          <p className="ct-hero-eyebrow ct-hero-sub">King & Queen · Reservations</p>
          <div className="ct-hero-title">
            <div className="ct-clip"><span className="ct-hero-line">Book Your</span></div>
            <div className="ct-clip"><span className="ct-hero-line ct-italic">Appointment</span></div>
          </div>
          <p className="ct-hero-tagline ct-hero-sub">
            Reserve your slot and experience luxury beauty services crafted just for you.
          </p>
        </div>
        <div className="ct-hero-scroll">
          <span>Scroll</span>
          <div className="ct-scroll-bar" />
        </div>
      </section>

      {/* STATEMENT SECTION */}
      <section className="ct-statement">
        <div className="ct-statement-inner ct-reveal">
          <p className="ct-statement-text">
            "We believe every appointment is the beginning of a transformation.
            Let us make your time at King &amp; Queen an experience you'll treasure."
          </p>
          <span className="ct-statement-attr">— King &amp; Queen Team</span>
        </div>
      </section>

      {/* MAP SECTION */}
      <section className="ct-map-section">
        <div className="ct-map-header ct-reveal">
          <div>
            <p className="ct-eyebrow">Find Us</p>
            <h2 className="ct-section-title">Our <em>Location</em></h2>
          </div>
          <a href="https://maps.app.goo.gl/Jzr7xPtCYWvJKtsj8" target="_blank" rel="noopener noreferrer" className="ct-directions-btn">
            Get Directions →
          </a>
        </div>
        <div className="ct-map-embed ct-reveal">
          <iframe
            src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3919.407377904935!2d76.7133769!3d10.7800783!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3ba86dfff9376cb3%3A0xbc1b43ffe80c8fbc!2sKing%20%26%20queen%20unisex%20beauty%20parlour!5e0!3m2!1sen!2sin!4v1773671554801!5m2!1sen!2sin"
            title="Salon Location" allowFullScreen="" loading="lazy" referrerPolicy="no-referrer-when-downgrade"
          />
        </div>
      </section>

      {/* BOOKING + INFO SECTION */}
      <section className="ct-booking-section">

        {/* LEFT — Booking Form */}
        <div className="ct-form-col" ref={formRef}>
          <div className="ct-reveal">
            <p className="ct-eyebrow">Reserve a Slot</p>
            <h2 className="ct-section-title">Slot <em>Registration</em></h2>
          </div>

          <div className="ct-form-card ct-reveal">
            <div className="ct-form-toprow">
              <p className="ct-form-hint">Fill in your details to book a time slot.</p>
              <button className="ct-view-btn" onClick={openTodayModal} disabled={loadingModal}>
                {loadingModal
                  ? <><span className="ct-spinner" /> Loading…</>
                  : <><img src="/assets/appnt.svg" className="ct-btn-icon" alt="" loading="lazy" /> View Appointments</>
                }
              </button>
            </div>

            {formStatus.submitted && (
              <div className={`ct-form-msg ${formStatus.success ? "ct-success" : "ct-error"}`}>
                {formStatus.message}
              </div>
            )}

            <form onSubmit={handleSubmit} className="ct-form">
              <div className="ct-field-group">

                {/* Name */}
                <div className="ct-field">
                  <label>Full Name <span className="ct-req">*</span></label>
                  <input type="text" value={name} onChange={(e) => setName(e.target.value)} placeholder="Your full name" required />
                </div>

                {/* Email */}
                <div className="ct-field">
                  <label>Email <span className="ct-req">*</span></label>
                  <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="your@email.com" required />
                </div>

                {/* Mobile */}
                <div className="ct-field ct-full">
                  <label>Mobile <span className="ct-req">*</span></label>
                  <PhoneInput value={phone} onChange={setPhone} />
                </div>

                {/* Date */}
                <div className="ct-field ct-full">
                  <label>Preferred Date <span className="ct-req">*</span></label>
                  <CustomCalendar
                    value={date}
                    onChange={setDate}
                    minDate={today}
                  />
                </div>

                {/* Service Required — Multi-Select with Search */}
                <div className="ct-field ct-full">
                  <label>Service(s) Required <span className="ct-req">*</span></label>
                  <MultiSelectDropdown
                    options={allServices}
                    selectedValues={services}
                    onChange={setServices}
                    placeholder="Search and select services..."
                  />
                  {services.length > 0 && (
                    <p className="ct-service-count">
                      ✓ {services.length} service{services.length !== 1 ? 's' : ''} selected
                    </p>
                  )}
                </div>

                {/* Time slot */}
                <div className="ct-field ct-full">
                  <label>Time Slot <span className="ct-req">*</span></label>
                  <select
                    value={slot}
                    onChange={(e) => setSlot(e.target.value)}
                    required
                    disabled={!date || loadingSlots || availableSlotsForDisplay.length === 0}
                  >
                    <option value="">
                      {!date ? "Select a date first" : loadingSlots ? "Loading slots…" : availableSlotsForDisplay.length === 0 ? "No slots available" : "Select a time slot"}
                    </option>
                    {date && !loadingSlots && availableSlotsForDisplay.map((s, i) => (
                      <option key={i} value={s}>{s}</option>
                    ))}
                  </select>
                </div>

              </div>

              <button
                type="submit"
                className="ct-submit-btn"
                disabled={!slot || services.length === 0 || availableSlotsForDisplay.length === 0 || phone.length !== 10}
              >
                Confirm Reservation {services.length > 0 && `(${services.length} service${services.length !== 1 ? 's' : ''})`}
              </button>
            </form>
          </div>
        </div>

        {/* RIGHT — Contact Info */}
        <div className="ct-info-col">
          <div className="ct-reveal">
            <p className="ct-eyebrow">Reach Us</p>
            <h2 className="ct-section-title">Contact <em>Info</em></h2>
          </div>
          <div className="ct-info-blocks ct-stagger-parent">
            <div className="ct-info-block ct-stagger-child">
              <div className="block1">
                <h4><span className="ct-info-icon">◎</span> Address</h4>
                <p>{shopDetails.name}</p>
                <p>{shopDetails.address}</p>
                <p>{shopDetails.area}</p>
              </div>
            </div>
            <div className="ct-info-block ct-stagger-child">
              <div className="block1">
                <h4><span className="ct-info-icon">✦</span> Contact</h4>
                <p><a href={`tel:${shopDetails.phone}`} className="ct-link">{shopDetails.phone}</a></p>
                <p><a href={`mailto:${shopDetails.email}`} className="ct-link">{shopDetails.email}</a></p>
                <p><a href={`https://wa.me/${shopDetails.whatsapp}`} target="_blank" rel="noopener noreferrer" className="ct-link ct-wap">WhatsApp →</a></p>
              </div>
            </div>
            <div className="ct-info-block ct-stagger-child">
              <div className="ct-hours1">
                <h4><span className="ct-info-icon">◈</span> Business Hours</h4>
                <div className="ct-hours">
                  {Object.entries(shopDetails.hours).map(([day, hrs]) => (
                    <div key={day} className="ct-hours-row">
                      <span>{day}</span>
                      <span>{hrs}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>

      </section>

      {/* SOCIAL SECTION */}
      <section className="ct-social-section">
        <div className="ct-reveal">
          <p className="ct-eyebrow" style={{ textAlign: "center" }}>Stay Connected</p>
          <h2 className="ct-section-title" style={{ textAlign: "center" }}>Follow <em>Our Journey</em></h2>
        </div>
        <div className="ct-social-grid ct-stagger-parent">
          {socialLinks.map((s, i) => (
            <a key={i} href={s.url} target="_blank" rel="noopener noreferrer"
              className="ct-social-card ct-stagger-child" style={{ "--social-color": s.color }}>
              <s.icon className="ct-social-icon" />
              <span className="ct-social-name">{s.name}</span>
            </a>
          ))}
        </div>
      </section>

      {/* MODAL */}
      {isModalOpen && (
        <div className="ct-modal-overlay" onClick={(e) => { if (e.target.classList.contains("ct-modal-overlay")) closeModal(); }}>
          <div className="ct-modal" ref={modalRef}>
            <div className="ct-modal-head">
              <div>
                <p className="ct-eyebrow" style={{ marginBottom: "4px" }}>Appointments</p>
                <h3 className="ct-modal-title">View Bookings</h3>
              </div>
              <button className="ct-modal-close" onClick={closeModal}>✕</button>
            </div>
            <div className="ct-modal-datepicker">
              <label>Select Date</label>
              <input type="date" value={selectedModalDate} onChange={handleModalDateChange} min={today} />
            </div>
            {modalDate && <p className="ct-modal-datetext">{formatDate(modalDate)}</p>}
            <div className="ct-modal-body">
              {loadingModal ? (
                <div className="ct-loading"><span className="ct-spinner" /><p>Loading…</p></div>
              ) : modalBookings.length > 0 ? (
                <div className="ct-modal-list">
                  {modalBookings.map((b, i) => (
                    <div key={b.id || i} className="ct-modal-item">
                      <div className="ct-modal-item-head">
                        <span className="ct-modal-time">{b.slot}</span>
                        <span className="ct-modal-id">#{b.id}</span>
                      </div>
                      <div className="ct-modal-detail">
                        <span className="ct-blabel">Booked at</span>
                        <span className="ct-bval">{new Date(b.created_at).toLocaleTimeString()}</span>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="ct-no-bookings" style={{ padding: "32px 0" }}>No appointments for this date.</p>
              )}
            </div>
            <div className="ct-modal-foot">
              <button className="ct-modal-close-btn" onClick={closeModal}>Close</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Contact;